import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormikContext } from "formik";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import {
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import ProConfirmation from "../../../utils/ProConfirmation";
import { PMDialog } from "../../../utils/PMDialog";
import { PMNotice } from "../../../utils/notifications";

const C = {
  accent: "#1463C7",
  ink: "#0F172A",
  body: "#475569",
  muted: "#64748B",
  faint: "#94A3B8",
  line: "#E2E8F0",
  line2: "#EEF1F5",
  bg: "#F8FAFC",
  soft: "#EAF3FD",
};

const ACCENT = {
  solid: "#1463C7",
  grad: "linear-gradient(135deg,#1463C7,#2D8BE8)",
  glow: "rgba(20,99,199,",
};

const cardStyle = {
  background: "#fff",
  border: `1px solid ${C.line}`,
  borderRadius: 16,
  boxShadow: "0 1px 2px rgba(16,28,46,.05)",
  overflow: "hidden",
};

const btnStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 7,
  padding: "9px 14px",
  borderRadius: 10,
  border: `1px solid ${C.line}`,
  background: "#fff",
  color: C.body,
  fontSize: 13.5,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
  whiteSpace: "nowrap",
};

const btnPrimaryStyle = {
  ...btnStyle,
  border: `1px solid ${C.accent}`,
  background: C.accent,
  color: "#fff",
  fontWeight: 700,
  boxShadow: "0 1px 2px rgba(20,99,199,.3)",
};

const btnSmallStyle = (variant = "neutral") => {
  const base = { ...btnStyle, padding: "7px 11px", fontSize: 12.5, gap: 6 };
  if (variant === "accent") return { ...base, border: `1px solid ${C.accent}`, color: C.accent, background: "#fff" };
  if (variant === "danger") return { ...base, border: "1px solid #FECACA", color: "#DC2626", background: "#fff" };
  return base;
};

const dsLabelStyle = { display: "block", fontSize: 13, fontWeight: 700, color: "#334155", margin: "0 0 9px" };
const dsFieldCardStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "14px 16px",
  border: "1.5px solid #E2E8F0",
  borderRadius: 14,
  background: "#fff",
  fontFamily: "inherit",
  fontSize: 16,
  color: C.ink,
  outline: "none",
};

const DialogCloseButton = ({ onClose, t }) => (
  <button
    type="button"
    onClick={onClose}
    aria-label={t("Lukk")}
    style={{
      width: 40,
      height: 40,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      border: "none",
      background: "#F1F5F9",
      borderRadius: 11,
      cursor: "pointer",
      color: "#475569",
      flex: "0 0 auto",
    }}
  >
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  </button>
);

const DeactivatedPill = ({ t }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      padding: "4px 10px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      background: C.line2,
      color: C.muted,
    }}
  >
    {t("user_profile.related_records.deactivated")}
  </span>
);

const rowKey = (row, index) => (row.id != null ? String(row.id) : row._tempId || `row-${index}`);

const isActiveRow = (row) => row.active !== false && row.delete !== true;

const normalizeRows = (rows) =>
  (Array.isArray(rows) ? rows : []).map((row, index) => ({
    ...row,
    active: row.active !== false,
    _tempId: row._tempId || (row.id == null ? `temp-${index}-${Date.now()}` : undefined),
  }));

export function UserRelatedRecordsList({
  name,
  setFieldValue: setFieldValueProp,
  values: valuesProp,
  isLoading,
  disabled = false,
  columns,
  emptyTitleKey,
  emptyDescKey,
  initialFormState,
  renderFormFields,
  validateForm,
  prepareRow,
  titleColumnId = "name",
  /** Bumps after a successful server save (e.g. user.version) to reload rows with ids. */
  syncKey,
  /** When set, dialog save/deactivate calls this immediately instead of deferring to Formik save bar. */
  onPersistRows,
}) {
  const { t } = useTranslation();
  const formik = useFormikContext();
  const setFieldValue = formik?.setFieldValue ?? setFieldValueProp;
  const setFormikState = formik?.setFormikState;
  const values = formik?.values ?? valuesProp;
  const fieldName = name;
  const sourceRows = values?.[fieldName];
  const lastPushedRef = useRef(null);
  const itemsRef = useRef([]);

  const [items, setItems] = useState(() => normalizeRows(sourceRows));
  itemsRef.current = items;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [formState, setFormState] = useState(initialFormState);
  const [formErrors, setFormErrors] = useState({});
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [persisting, setPersisting] = useState(false);

  // After save, parent bumps syncKey — adopt authoritative rows (incl. server ids).
  useEffect(() => {
    if (syncKey === undefined) return;
    lastPushedRef.current = null;
    setItems(normalizeRows(sourceRows));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when syncKey changes only
  }, [syncKey]);

  // Pull external Formik values unless local edits are still ahead of the parent field.
  useEffect(() => {
    const external = sourceRows || [];
    const externalKey = JSON.stringify(external);
    if (lastPushedRef.current === externalKey) {
      lastPushedRef.current = null;
      return;
    }

    const localPayload = itemsRef.current.map(({ _tempId, ...row }) => row);
    if (JSON.stringify(localPayload) === externalKey) return;
    // Dialog save updates `items` before Formik catches up — do not wipe pending rows.
    if (localPayload.length > external.length) return;
    if (
      localPayload.length === external.length &&
      JSON.stringify(localPayload) !== externalKey
    ) {
      return;
    }

    setItems(normalizeRows(external));
  }, [JSON.stringify(sourceRows || [])]);

  const rowsPayload = useCallback(
    (rows) => rows.map(({ _tempId, ...row }) => row),
    []
  );

  /** Push local rows into the parent Formik form (must run on dialog save, not only in effects). */
  const pushToFormik = useCallback(
    (nextItems) => {
      const payload = rowsPayload(nextItems);
      const payloadKey = JSON.stringify(payload);
      lastPushedRef.current = payloadKey;

      if (setFormikState) {
        setFormikState((prev) => ({
          ...prev,
          values: {
            ...prev.values,
            [fieldName]: payload,
          },
        }));
      } else if (setFieldValue) {
        setFieldValue(fieldName, payload);
      }
    },
    [fieldName, rowsPayload, setFieldValue, setFormikState]
  );

  // Keep Formik in sync when `items` changes locally and persistence is deferred to the save bar.
  useEffect(() => {
    if (onPersistRows) return;
    const payload = rowsPayload(items);
    const external = sourceRows || [];
    if (JSON.stringify(payload) !== JSON.stringify(external)) {
      pushToFormik(items);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sourceRows intentionally omitted
  }, [items, fieldName, onPersistRows, pushToFormik, rowsPayload]);

  const activeCount = useMemo(() => items.filter(isActiveRow).length, [items]);

  const openCreate = () => {
    setEditingRow(null);
    setFormState(initialFormState);
    setFormErrors({});
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    if (!isActiveRow(row) || disabled) return;
    setEditingRow(row);
    setFormState(prepareRow(row, "form"));
    setFormErrors({});
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingRow(null);
    setFormErrors({});
  };

  const applyRows = useCallback(
    (nextItems) => {
      const normalized = normalizeRows(nextItems);
      setItems(normalized);
      pushToFormik(normalized);
    },
    [pushToFormik]
  );

  const persistRows = useCallback(
    async (nextItems, action = "save") => {
      if (!onPersistRows) {
        applyRows(nextItems);
        return true;
      }

      setPersisting(true);
      try {
        const serverRows = await onPersistRows(fieldName, rowsPayload(nextItems));
        applyRows(serverRows ?? nextItems);
        toast.success(t(`user_profile.related_records.${action}_success`));
        return true;
      } catch (err) {
        const message =
          err?.message && err.message !== "failed"
            ? err.message
            : t(`user_profile.related_records.${action}_failed`);
        toast.error(message);
        return false;
      } finally {
        setPersisting(false);
      }
    },
    [applyRows, fieldName, onPersistRows, rowsPayload, t]
  );

  const handleSave = async () => {
    const errors = validateForm(formState, t) || {};
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const rowData = prepareRow(formState, "row", editingRow);
    let nextItems;
    if (editingRow) {
      nextItems = items.map((item) =>
        rowKey(item) === rowKey(editingRow)
          ? { ...item, ...rowData, active: true, delete: false }
          : item
      );
    } else {
      nextItems = [
        ...items,
        { ...rowData, id: null, active: true, _tempId: `temp-${Date.now()}` },
      ];
    }

    const ok = await persistRows(nextItems, "save");
    if (ok) closeDialog();
  };

  const requestDelete = (row) => {
    if (!isActiveRow(row) || disabled) return;
    setSelectedRow(row);
    setDeleteConfirmationOpen(true);
  };

  const handleDeactivate = async () => {
    setDeleteConfirmationOpen(false);
    if (!selectedRow) return;
    const nextItems = items.map((item) =>
      rowKey(item) === rowKey(selectedRow)
        ? { ...item, active: false, delete: true }
        : item
    );
    const ok = await persistRows(nextItems, "deactivate");
    if (ok) setSelectedRow(null);
  };

  const renderCellValue = (column, row) => {
    if (column.renderValue) return column.renderValue(row, t);
    const value = row[column.id];
    if (value === null || value === undefined || value === "") {
      return <span style={{ color: C.faint }}>{t("user_profile.empty_value")}</span>;
    }
    if (typeof value === "boolean") return value ? t("Yes") : t("No");
    return String(value);
  };

  const renderRowActions = (row) => {
    if (!isActiveRow(row) || disabled) return null;
    return (
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, justifyContent: "flex-end" }}>
        <button type="button" onClick={() => openEdit(row)} style={btnSmallStyle("accent")}>
          <PencilSquareIcon style={{ width: 15, height: 15 }} />
          {t("user_profile.related_records.edit")}
        </button>
        <button type="button" onClick={() => requestDelete(row)} style={btnSmallStyle("danger")}>
          <TrashIcon style={{ width: 15, height: 15 }} />
          {t("user_profile.related_records.delete")}
        </button>
      </div>
    );
  };

  if (isLoading) {
    return <ResourceLoading />;
  }

  return (
    <>
      <div style={cardStyle}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "13px 16px",
            borderBottom: `1px solid ${C.line2}`,
          }}
        >
          <span style={{ fontSize: 12.5, color: C.faint, fontWeight: 600 }}>
            {activeCount} {t("user_profile.related_records.active_count")}
          </span>
          <span style={{ flex: 1 }} />
          {!disabled && (
            <button type="button" onClick={openCreate} style={btnPrimaryStyle}>
              <PlusIcon style={{ width: 16, height: 16 }} />
              {t("user_profile.related_records.add")}
            </button>
          )}
        </div>

        {items.length === 0 ? (
          <div style={{ padding: "32px 16px" }}>
            <PMNotice title={t(emptyTitleKey)} description={t(emptyDescKey)} />
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block">
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: `${columns.map(() => "minmax(0,1fr)").join(" ")} auto`,
                  gap: 12,
                  padding: "11px 16px",
                  borderBottom: `1px solid ${C.line2}`,
                  background: C.bg,
                }}
              >
                {columns.map((column) => (
                  <span key={column.id} style={{ fontSize: 12.5, fontWeight: 700, color: C.muted }}>
                    {t(column.labelKey)}
                  </span>
                ))}
                <span style={{ fontSize: 12.5, fontWeight: 700, color: C.muted, textAlign: "right" }}>
                  {t("user_profile.related_records.actions")}
                </span>
              </div>
              {items.map((row, idx) => {
                const active = isActiveRow(row);
                return (
                  <div
                    key={rowKey(row, idx)}
                    style={{
                      display: "grid",
                      gridTemplateColumns: `${columns.map(() => "minmax(0,1fr)").join(" ")} auto`,
                      gap: 12,
                      alignItems: "center",
                      padding: "13px 16px",
                      borderTop: idx === 0 ? "none" : `1px solid ${C.line2}`,
                      opacity: active ? 1 : 0.55,
                      background: active ? "#fff" : C.bg,
                    }}
                  >
                    {columns.map((column) => (
                      <div key={column.id} style={{ minWidth: 0, fontSize: 14, color: active ? C.ink : C.muted }}>
                        {renderCellValue(column, row)}
                      </div>
                    ))}
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
                      {!active && <DeactivatedPill t={t} />}
                      {renderRowActions(row)}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Mobile cards — use Tailwind for layout; inline display would override md:hidden */}
            <div className="flex flex-col gap-2.5 p-3 md:hidden">
              {items.map((row, idx) => {
                const active = isActiveRow(row);
                const titleColumn = columns.find((c) => c.id === titleColumnId) || columns[0];
                const detailColumns = columns.filter((c) => c.id !== (titleColumn?.id || titleColumnId));
                return (
                  <div
                    key={rowKey(row, idx)}
                    style={{
                      border: `1px solid ${C.line}`,
                      borderRadius: 14,
                      padding: 14,
                      background: active ? "#fff" : C.bg,
                      opacity: active ? 1 : 0.55,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, marginBottom: 10 }}>
                      <div style={{ minWidth: 0 }}>
                        <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: active ? C.ink : C.muted }}>
                          {titleColumn ? renderCellValue(titleColumn, row) : row.name}
                        </p>
                        {!active && (
                          <div style={{ marginTop: 6 }}>
                            <DeactivatedPill t={t} />
                          </div>
                        )}
                      </div>
                      {renderRowActions(row)}
                    </div>
                    {detailColumns.map((column) => (
                      <div key={column.id} style={{ marginTop: 8 }}>
                        <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: C.muted }}>{t(column.labelKey)}</p>
                        <p style={{ margin: "2px 0 0", fontSize: 14, color: active ? C.body : C.muted }}>
                          {renderCellValue(column, row)}
                        </p>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      <PMDialog open={dialogOpen} onClose={closeDialog} dense size="lg">
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div
            className="flex-shrink-0 px-2 sm:px-0"
            style={{ display: "flex", alignItems: "center", gap: 12, paddingBottom: 14 }}
          >
            <DialogCloseButton onClose={closeDialog} t={t} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: C.ink, letterSpacing: "-.01em" }}>
                {editingRow
                  ? t("user_profile.related_records.dialog.edit_title")
                  : t("user_profile.related_records.dialog.create_title")}
              </div>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain px-2 sm:px-0">
            <div
              style={{
                background: C.bg,
                borderRadius: 16,
                padding: 16,
                display: "flex",
                flexDirection: "column",
                gap: 20,
                textAlign: "left",
              }}
            >
              {renderFormFields({ formState, setFormState, formErrors, t, dsLabelStyle, dsFieldCardStyle })}
            </div>
          </div>

          <div
            className="flex-shrink-0 px-2 pb-2 sm:px-0 sm:pb-0"
            style={{
              display: "flex",
              gap: 12,
              paddingTop: 12,
              marginTop: 8,
              borderTop: `1px solid ${C.line2}`,
              background: "#fff",
            }}
          >
            <button
              type="button"
              onClick={closeDialog}
              style={{
                padding: "16px 22px",
                borderRadius: 14,
                border: "1.5px solid #E2E8F0",
                background: "#fff",
                color: C.body,
                fontWeight: 700,
                fontSize: 16,
                cursor: "pointer",
                fontFamily: "inherit",
                flex: "0 0 auto",
              }}
            >
              {t("user_profile.related_records.dialog.cancel")}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={persisting}
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                padding: 16,
                borderRadius: 14,
                border: "none",
                color: "#fff",
                fontWeight: 800,
                fontSize: 16,
                cursor: persisting ? "not-allowed" : "pointer",
                opacity: persisting ? 0.7 : 1,
                fontFamily: "inherit",
                background: ACCENT.grad,
                boxShadow: `0 12px 24px -8px ${ACCENT.glow}.6)`,
              }}
            >
              {persisting
                ? t("user_profile.related_records.dialog.saving")
                : t("user_profile.related_records.dialog.save")}
            </button>
          </div>
        </div>
      </PMDialog>

      <ProConfirmation
        severity="confirmation"
        open={deleteConfirmationOpen}
        message={t("user_profile.related_records.deactivate_confirm")}
        action="delete"
        module="order_item"
        yesCallback={handleDeactivate}
        cancelCallback={() => setDeleteConfirmationOpen(false)}
      />
    </>
  );
}

export { dsLabelStyle, dsFieldCardStyle, C };
